import { Injectable, signal, computed } from '@angular/core';
import { HttpHeaders, httpResource } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';

export interface TradelineOutcome {
  tradelineId: string;
  interestRateUsed: number;
  monthlyPayment: number;
  paidPrincipal: number;
  paidInterest: number;
  payoffPeriod: string;
}

export interface Plan {
  maxPayoffMonths: number;
  maxPayoffPeriod: string;
  totalInterestPaid: number;
  totalMonthlyPayment: number;
  totalPaid: number;
  totalPrincipalPaid: number;
  revolvingCreditUtilization: number;
  tradelineOutcomes: TradelineOutcome[];
}

export interface Offer {
  productType: string;
  subProductType: string;
  offerType: string;
  offerUrl: string;
  heading: string;
  label: string;
  callToAction: string;
  callToActionSubtext: string | null;
  minLoanAmount: number;
  maxAmount: number;
  minApr: number;
  maxApr: number;
}

export interface OfferSummary {
  offer?: Offer;
  loanAmountUsed?: number;
  aprUsed?: number;
  termUsed?: number;
  offerMonthlyPayment?: number;
  interestPaymentChange?: number;
  totalMonthlyPaymentChange?: number;
  maxPayoffMonthsChange?: number;
  creditUtilizationPercentagePointChange?: number;
}

export interface DebtConsolidationResponse {
  cid?: string;
  consolidationId?: string;
  calculationTimestamp?: string;
  creditReportKey?: string;
  currentPlan?: Plan;
  offeredPlan?: Plan;
  offerSummary?: OfferSummary;
}

@Injectable({
  providedIn: 'root',
})
export class DebtConsolidationService {
  private readonly fetchRequested = signal(false);

  private readonly resource = httpResource<DebtConsolidationResponse>(() => {
    if (!this.fetchRequested()) return undefined;
    return {
      url: `${environment.customerGateway}/debt-consolidation`,
      headers: new HttpHeaders().set(
        'Authorization',
        this.cookieService.get('X-Access-Token')
      ),
    };
  });

  readonly status = this.resource.status;
  readonly isLoaded = computed(
    () =>
      this.resource.status() !== 'loading' &&
      this.resource.status() !== 'reloading'
  );
  readonly hasValue = computed(() => this.resource.hasValue());
  readonly value = computed(() =>
    this.resource.hasValue() ? this.resource.value() : undefined
  );
  readonly debtConsolidationValue = computed(() =>
    this.resource.hasValue() ? this.resource.value() : undefined
  );
  readonly error = computed(() => this.resource.error());

  constructor(private readonly cookieService: CookieService) {}

  requestDebtConsolidation(): void {
    this.fetchRequested.set(true);
  }
}
